import React, { useEffect, useMemo, useState } from "react";
import SummaryCards from "../../components/Common/SummaryCards";
import {
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
} from "../../components/Common/AppOverlay";
import { showLoading } from "react-global-loading";
import { ErrorNotify } from "../../shared/Alert";
import ActionMenu from "../../components/Common/ActionMenu";
import useAdminDataLifecycle from "../../components/Common/useAdminDataLifecycle";
import { getFinancialYearList } from "../../helpers/backend_helper";
import CustomDataGrid from "../../components/Common/CustomDataGrid";
import FinancialYearDetail from "./FinancialYearDetail";
import "../Standards/Standards.scss";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.valueOf())
    ? value
    : date.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });
};

const FinancialYearActionMenu = ({
  financialYear,
  onEdit,
  lifecycleActions,
}) => {
  return (
    <ActionMenu
      label={financialYear.name}
      triggerClassName="masters-grid-action"
      actions={[
        {
          label: "Edit details",
          icon: <i className="bx bx-edit-alt" />,
          onSelect: () => onEdit(financialYear.id),
        },
        ...lifecycleActions,
      ]}
    />
  );
};

const FinancialYearList = ({ createRequest = 0 }) => {
  const [financialYears, setFinancialYears] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [detailId, setDetailId] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");

  const loadData = () => {
    showLoading(true);
    getFinancialYearList()
      .then((response) => setFinancialYears(response?.data || []))
      .catch(() =>
        ErrorNotify("Error occurred while fetching financial years.")
      )
      .finally(() => showLoading(false));
  };
  const lifecycle = useAdminDataLifecycle(loadData);

  const openDetail = (id = 0) => {
    setDetailId(id);
    setIsDrawerOpen(true);
  };

  const closeDetail = () => {
    setIsDrawerOpen(false);
    setDetailId(0);
    loadData();
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (createRequest) openDetail();
  }, [createRequest]);

  const counts = useMemo(
    () => ({
      total: financialYears.length,
      active: financialYears.filter((item) => item.status === 1).length,
      inactive: financialYears.filter((item) => item.status !== 1).length,
      documented: financialYears.filter((item) => item.description).length,
    }),
    [financialYears]
  );

  const filteredFinancialYears = useMemo(
    () =>
      financialYears.filter(
        (item) =>
          `${item.name || ""} ${item.description || ""}`
            .toLowerCase()
            .includes(search.toLowerCase()) &&
          (status === "all" || String(item.status) === status)
      ),
    [financialYears, search, status]
  );

  const columns = [
    {
      id: "name",
      header: "Financial Year",
      accessorKey: "name",
      minSize: 200,
      flex: 1.1,
      cell: ({ row, getValue }) => (
        <button
          className="masters-financial-year__name"
          type="button"
          onClick={() => openDetail(row.original.id)}
        >
          <span>{(getValue() || "F").charAt(0)}</span>
          {getValue() || "Unnamed financial year"}
        </button>
      ),
    },
    {
      id: "startDate",
      header: "Start Date",
      accessorFn: (row) => formatDate(row.startDate),
      minSize: 145,
      flex: 0.8,
    },
    {
      id: "endDate",
      header: "End Date",
      accessorFn: (row) => formatDate(row.endDate),
      minSize: 145,
      flex: 0.8,
    },
    {
      id: "description",
      header: "Description",
      accessorKey: "description",
      minSize: 250,
      flex: 1.4,
      cell: ({ getValue }) => (
        <span className="standards-grid__description">{getValue() || "—"}</span>
      ),
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (row) => (row.status === 1 ? "Active" : "Inactive"),
      minSize: 110,
      flex: 0.65,
      cell: ({ row, getValue }) => (
        <span
          className={`standards-grid__status standards-grid__status--${
            row.original.status === 1 ? "active" : "archived"
          }`}
        >
          {getValue()}
        </span>
      ),
    },
    {
      id: "updatedOn",
      header: "Last Updated",
      accessorFn: (row) => formatDate(row.updatedOn),
      minSize: 150,
      flex: 0.8,
    },
    {
      id: "actions",
      header: "Actions",
      accessorFn: () => "",
      enableSorting: false,
      enableColumnFilter: false,
      enableExport: false,
      minSize: 86,
      flex: 0,
      cell: ({ row }) => (
        <FinancialYearActionMenu
          financialYear={row.original}
          onEdit={openDetail}
          lifecycleActions={lifecycle.actions("financial-year", row.original)}
        />
      ),
    },
  ];

  return (
    <section className="standards-page__content masters-list masters-financial-year">
      <header className="masters-list__header">
        <div>
          <h1 className="piers-page-title">
            Financial Years <i className="bx bx-info-circle" />
          </h1>
          <p>
            Manage the financial periods used for engagement planning and
            reporting.
          </p>
        </div>
      </header>
      <SummaryCards
        items={[
          [
            "bx-calendar",
            "blue",
            "Total Financial Years",
            counts.total,
            "All configured financial years",
          ],
          [
            "bx-check-circle",
            "green",
            "Active Financial Years",
            counts.active,
            `${
              counts.total
                ? Math.round((counts.active / counts.total) * 100)
                : 0
            }% of total`,
          ],
          [
            "bx-pause-circle",
            "orange",
            "Inactive Financial Years",
            counts.inactive,
            `${
              counts.total
                ? Math.round((counts.inactive / counts.total) * 100)
                : 0
            }% of total`,
          ],
          [
            "bx-detail",
            "purple",
            "Descriptions Completed",
            counts.total
              ? `${Math.round((counts.documented / counts.total) * 100)}%`
              : "0%",
            "All financial years documented",
          ],
        ]}
        ariaLabel="Financial year summary"
      />
      <section className="standards-grid-panel">
        
        <CustomDataGrid
 filterContent={<section
          className="standards-filter-panel"
          aria-label="Financial year filters"
        >
          <div className="standards-filter-panel__fields">
            <label>
              <span>Search</span>
              <div className="standards-filter-panel__search">
                <i className="bx bx-search" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search financial years..."
                />
              </div>
            </label>
            <label>
              <span>Status</span>
              <select
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </label>
          </div>
          <div className="standards-filter-panel__actions">
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setStatus("all");
              }}
            >
              <i className="bx bx-filter-alt" /> Clear Filters
            </button>
          </div>
        </section>}

          data={filteredFinancialYears}
          columns={columns}
          onRefresh={loadData}
          fileName="financial-years"
          recordLabel="financial years"
          showToolbar={false}
          initialPageSize={15}
          rowHeight={56}
          columnHeaderHeight={56}
          gridSx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#ffffff !important",
              borderBottom: "1px solid #e7ebf2",
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader--sortable":
              {
                backgroundColor: "#ffffff !important",
                color: "#52627f",
              },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#52627f",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: ".2px",
            },
            "& .MuiDataGrid-columnSeparator": { color: "#e7ebf2" },
            "& .MuiDataGrid-cell": {
              fontSize: 13,
              borderBottomColor: "#edf0f5",
              color: "#273957",
            },
            "& .MuiDataGrid-row": { minHeight: "56px !important" },
            "& .MuiDataGrid-row:hover": { backgroundColor: "#f8faff" },
            "& .MuiDataGrid-footerContainer, & .MuiDataGrid-footerContainer .MuiTypography-root":
              {
                fontSize: 14,
                borderTopColor: "#e7ebf2",
              },
          }}
        />
      </section>

      <Offcanvas
        backdrop="static"
        className="standards-drawer"
        direction="end"
        isOpen={isDrawerOpen}
        toggle={closeDetail}
      >
        <OffcanvasHeader toggle={closeDetail} className="canvasHeader">
          {detailId ? "Edit Financial Year" : "New Financial Year"}
        </OffcanvasHeader>
        <OffcanvasBody>
          <FinancialYearDetail detailId={detailId} closeAction={closeDetail} />
        </OffcanvasBody>
      </Offcanvas>
      {lifecycle.dialog}
    </section>
  );
};

export default FinancialYearList;
